/**
 * BottomSheet — sliding sheet anchored to the bottom of the viewport
 * on mobile, falls back to a centered Dialog on tablet+ widths.
 *
 * Why this exists (iter-169zr · Fase 2):
 *   Filters / quick-add / multi-step pickers feel native on mobile when
 *   they slide from the bottom (one-handed reach, thumb-friendly).
 *   On desktop the same content is more readable as a centered dialog.
 *   This component is the single place where that decision is made so
 *   pages don't have to reimplement the responsive switch.
 *
 * Usage:
 *   <BottomSheet open={open} onOpenChange={setOpen} title="Filtros">
 *     ...content...
 *   </BottomSheet>
 *
 * The component leans on Radix Dialog (already a peer-dep through
 * shadcn) so we get focus trap, ESC handling and scroll lock for free.
 * The CSS layer below positions it differently per breakpoint.
 */
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

export function BottomSheet({
  open, onOpenChange,
  title, description,
  children,
  footer,
  size = "md",
  testid = "bottom-sheet",
}) {
  const widthClass = {
    sm: "md:max-w-sm",
    md: "md:max-w-md",
    lg: "md:max-w-lg",
    xl: "md:max-w-xl",
  }[size] || "md:max-w-md";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          data-testid={`${testid}-overlay`}
        />
        <DialogPrimitive.Content
          data-testid={testid}
          className={cn(
            "fixed z-[70] bg-white shadow-xl border border-slate-200 outline-none flex flex-col overflow-hidden",
            // iter-170m — Mobile + tablet (< md, < 768px): full-width
            // bottom-sheet anchored to viewport. We pad the bottom by the
            // mobile-nav height + safe-area so the footer is NEVER hidden
            // by the bottom nav (the nav is z-[60], we are z-[70], but
            // some Android browsers compose them anyway when the sheet
            // doesn't span past the nav). Adding mb keeps the visible
            // sheet ABOVE the nav by design instead of fighting z-order.
            "inset-x-0 bottom-0 max-h-[92vh] rounded-t-3xl",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom",
            // Desktop (≥ md, ≥768px): centered dialog. Breakpoint raised
            // from sm to md so the bottom-anchored layout still applies
            // on iPad-mini / foldables / 7" tablets where the previous
            // ``sm:`` centered modal was getting clipped by the mobile
            // bottom nav (which is shown up to lg, ≥1024px).
            "md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
            "md:w-full md:rounded-2xl md:max-h-[85vh]",
            widthClass,
          )}
        >
          {/* Drag handle (mobile/tablet only) — purely visual, no actual drag.
              Lets the user know the sheet is dismissible by tap-on-overlay.
              iter-170m: hidden at md+ (was sm:hidden). */}
          <div className="md:hidden flex justify-center pt-2.5 pb-1 shrink-0">
            <span className="h-1 w-10 rounded-full bg-slate-300" aria-hidden="true" />
          </div>

          <div className="px-5 md:px-6 pt-3 md:pt-5 pb-3 flex items-start justify-between gap-3 shrink-0">
            <div className="min-w-0">
              {title && (
                <DialogPrimitive.Title
                  className="text-base md:text-lg font-bold tracking-tight text-slate-900"
                  data-testid={`${testid}-title`}
                >
                  {title}
                </DialogPrimitive.Title>
              )}
              {description && (
                <DialogPrimitive.Description className="mt-0.5 text-xs md:text-sm text-slate-500">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              className="shrink-0 h-9 w-9 -mr-1 -mt-1 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 inline-flex items-center justify-center transition-colors"
              data-testid={`${testid}-close`}
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-5 md:px-6 pb-5">
            {children}
          </div>

          {footer && (
            <div
              className="shrink-0 px-5 md:px-6 py-3 border-t border-slate-200 bg-white lumfi-thumb-zone"
              data-testid={`${testid}-footer`}
            >
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
